import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { AssignmentInd } from '@mui/icons-material';
import SideBarContainer from 'Containers/SideBarContainer';
import useTranslation from 'Hooks/useTranslation';
import usePageSize from 'Hooks/usePageSize';
import { Button, ConfirmDialog, Pagination, SearchBox, FilterSelect } from 'Components/ui';
import CoursesTable from 'Components/CoursesTable';
import CoursesAccordion from 'Components/CoursesAccordion';
import {
  Header,
  LoadingState,
  PageBody,
  SelectionNote,
  Stack,
  Subtitle,
  Title,
  Toolbar,
  ToolbarEnd,
} from 'Components/ui/layout';
import useCourse from './Hooks/useCourse';

const DEFAULT_PAGE_SIZE = 10;

/**
 * Cursos obligatorios para un puesto.
 *
 * Arriba, plegados, los cursos que el empleado ya arrastra por su empresa y por
 * su departamento: no se definen aquí, pero verlos evita asignar dos veces lo
 * mismo. Abajo, el catálogo que sí se define, con el mismo
 * marcar-y-guardar-diferencia del resto de las pantallas de definición.
 */
export default function DefinitionJobTitle() {
  const location = useLocation();
  const { t2 } = useTranslation('DefinitionJobTitle');
  const jobTitleId = location?.state?.jobTitle;
  const departmentId = location?.state?.department;

  const [isLoading, setIsLoading] = useState(true);
  const { courses, terminals, categories, saveCourses, coursesByEnterprise, coursesByDepartment, defaultSelectedIds } =
    useCourse({ departmentId, jobTitleId }, setIsLoading);

  const [search, setSearch] = useState('');
  const [terminalFilter, setTerminalFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  // La selección arranca en lo que ya estaba asignado; a partir de ahí manda el
  // usuario. Sin esto el primer guardado desasignaría todo lo ya definido.
  useEffect(() => {
    setSelected(new Set(defaultSelectedIds));
  }, [defaultSelectedIds]);

  useEffect(() => {
    setPage(1);
  }, [search, terminalFilter, categoryFilter]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return courses.filter(course => {
      const matchesSearch = needle === '' || course.name.toLowerCase().includes(needle);
      const matchesTerminal =
        terminalFilter.length === 0 || terminalFilter.some(name => course.terminalNames.includes(name));
      const matchesCategory = categoryFilter.length === 0 || categoryFilter.includes(course.categoryName);
      return matchesSearch && matchesTerminal && matchesCategory;
    });
  }, [courses, search, terminalFilter, categoryFilter]);

  const [pageSize, setPageSizeState] = usePageSize('definitionCoursesJobTitle', DEFAULT_PAGE_SIZE);

  // Cambiar el tamaño vuelve a la primera página: quien estaba en la 7 con 10
  // por página se saldría del rango al pedir 100, y la tabla saldría vacía.
  const setPageSize = value => {
    setPageSizeState(value);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  const toggle = courseId => {
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(courseId)) next.delete(courseId);
      else next.add(courseId);
      return next;
    });
  };

  // Alcanza solo a las filas visibles: marcar de golpe cursos que el filtro
  // esconde es justo lo que nadie espera de una casilla del encabezado.
  const toggleAll = () => {
    setSelected(previous => {
      const next = new Set(previous);
      const all = visible.length > 0 && visible.every(course => previous.has(course.id));
      visible.forEach(course => (all ? next.delete(course.id) : next.add(course.id)));
      return next;
    });
  };

  // El backend recibe la diferencia, no la foto completa.
  const changes = useMemo(() => {
    const defaults = new Set(defaultSelectedIds);

    return [
      ...[...selected].filter(id => !defaults.has(id)).map(id => ({ id, value: true })),
      ...defaultSelectedIds.filter(id => !selected.has(id)).map(id => ({ id, value: false })),
    ];
  }, [selected, defaultSelectedIds]);

  const added = changes.filter(change => change.value).length;
  const removed = changes.length - added;
  const confirmDescription = [
    added > 0 ? `${added} ${t2('ConfirmSaveAdded')}` : null,
    removed > 0 ? `${removed} ${t2('ConfirmSaveRemoved')}` : null,
  ]
    .filter(Boolean)
    .join('. ');

  const confirmSave = () => {
    setConfirmOpen(false);
    saveCourses(changes);
  };

  const inheritedLabels = { name: t2('Name'), terminal: t2('Terminal'), duration: t2('TimeCourse') };

  if (location?.state?.isComingFrom !== '/definicion/seleccion/puesto') {
    return <Redirect to="/definicion" />;
  }

  return (
    <SideBarContainer>
      <PageBody>
        <Header>
          <div>
            <Title>{t2('Title')}</Title>
            <Subtitle>{t2('SubTitle')}</Subtitle>
          </div>
          <Button type="button" variant="primary" disabled={changes.length === 0} onClick={() => setConfirmOpen(true)}>
            {t2('ButtonSave')}
          </Button>
        </Header>

        <Stack>
          <CoursesAccordion
            title={t2('AcordionCompany')}
            rows={coursesByEnterprise}
            emptyLabel={t2('AcordionEmpty')}
            labels={inheritedLabels}
          />
          <CoursesAccordion
            title={t2('AcordionDepartment')}
            rows={coursesByDepartment}
            emptyLabel={t2('AcordionEmpty')}
            labels={inheritedLabels}
          />
        </Stack>

        <Toolbar>
          <SearchBox value={search} placeholder={t2('Search')} onChange={setSearch} />
          <ToolbarEnd>
            <SelectionNote>
              {selected.size} {t2('SelectedCourses')}
            </SelectionNote>
            <FilterSelect
              value={terminalFilter}
              options={terminals.map(terminal => ({ value: terminal, label: terminal }))}
              placeholder={t2('Terminal')}
              onChange={setTerminalFilter}
            />
            <FilterSelect
              value={categoryFilter}
              options={categories.map(category => ({ value: category, label: category }))}
              placeholder={t2('Category')}
              onChange={setCategoryFilter}
            />
          </ToolbarEnd>
        </Toolbar>

        {isLoading ? (
          <LoadingState>{t2('Loading')}</LoadingState>
        ) : (
          <>
            <CoursesTable
              rows={visible}
              labels={{
                name: t2('Name'),
                terminal: t2('Terminal'),
                category: t2('Category'),
                duration: t2('TimeCourse'),
                empty: t2('Empty'),
                selectAll: t2('SelectAll'),
              }}
              selected={selected}
              onToggle={toggle}
              onToggleAll={toggleAll}
            />
            {filtered.length > 0 ? (
              <Pagination
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                totalItems={filtered.length}
                onChange={setPage}
                onPageSizeChange={setPageSize}
                labels={{
                  showing: t2('PaginationShowing'),
                  of: t2('PaginationOf'),
                  page: t2('PaginationPage'),
                  prev: t2('PaginationPrev'),
                  next: t2('PaginationNext'),
                  first: t2('PaginationFirst'),
                  last: t2('PaginationLast'),
                  perPage: t2('PaginationPerPage'),
                }}
              />
            ) : null}
          </>
        )}
      </PageBody>

      {/* Guardar cambia lo que tiene que aprobar todo un puesto: demasiado
          ancho para dispararse de un solo clic. */}
      <ConfirmDialog
        open={confirmOpen}
        tone="primary"
        icon={<AssignmentInd />}
        title={t2('ConfirmSaveTitle')}
        description={confirmDescription}
        cancelLabel={t2('ConfirmCancel')}
        confirmLabel={t2('ConfirmAccept')}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmSave}
      />
    </SideBarContainer>
  );
}
